import { z } from 'zod'
import { enumerated } from '~/prix/types/enumerated'
import { spaceTypeSingularLabels } from '../variable/spaceTime.data'
import { dateOrIsoString } from '~/prix/types/date'
import { sync } from 'glob'

export const questionOptionSchema = z.object({
  id: z.number().describe('ID da opção').nullish(),
  label: z.string().describe('Rótulo da opção'),
  deletedAt: z.string().datetime().describe('Data de remoção da opção').nullish(),
})

export const questionSchema = z.object({
  id: z.number().nullish(),
  parentQuestionId: z.number().nullish(),
  title: z.string().describe('Título da questão'),
  observation: z.string().describe('Observação da questão').nullish(),
  lgpdDisclaimer: z.string().describe('Avido legal LGPD').nullish(),
  type: enumerated({
    optionButton: 'option_button',
    freeText: 'free_text',
    checkBox: 'check_box',
    numeric: 'numeric',
    select: 'select',
    date: 'date',
    boolean: 'boolean',
  }).nullish(),
  required: z.boolean().describe('Campo obrigatório/opcional').nullish(),
  ordinalPosition: z.coerce.number().describe('Posição da questão no questionário').nullish(),
  startDate: z.string().date().describe('Início do questionário').nullish(),
  endDate: z.string().date().describe('Data de término do questionário').nullish(),
  deletedAt: z.string().datetime().describe('Data de remoção da questão').nullish(),
  questionOptions: z
    .array(questionOptionSchema)
    .describe('Opções da questão de múltiplas escolhas')
    .nullish(),
  questionnaireId: z.number().describe('ID do Questionário').nullish(),
})

export type QuestionType = z.infer<typeof questionSchema> & {
  questions?: z.infer<typeof questionSchema>[] | null
  childQuestions?: z.infer<typeof questionSchema>[] | null
}

export const questionObject: z.ZodType<QuestionType> = questionSchema.extend({
  questions: z.array(questionSchema).nullish(),
  childQuestions: z.array(questionSchema).describe('Questões filho').nullish(),
})

export const questionnaireObject = z.object({
  id: z.number().describe('Identificador'),
  title: z.string().describe('Título do questionário'),
  observation: z.string().describe('Observação do questionário').nullish(),
  lgpdDisclaimer: z.string().describe('Avido legal LGPD').nullish(),
  targetPublic: z
    .object({})
    .describe('Público alvo do questionário')
    .transform(val => JSON.stringify(val))
    .nullish(), // @TODO: esse parâmetro é um objeto
  delimitationType: enumerated(spaceTypeSingularLabels).nullish(),
  targetType: enumerated({
    residence: 'Domicílio',
    family: 'Família',
    citizen: 'Cidadão',
  }).nullish(),
  delimitationId: z.string().describe('ID da demarcação escolhida').nullish(),
  delimitationCustom: z.string().describe('Polígono da demarcação').nullish(),
  startDate: dateOrIsoString().describe('Início do questionário').nullish(),
  endDate: dateOrIsoString().describe('Data de término do questionário').nullish(),
  deletedAt: dateOrIsoString().describe('Data de remoção do questionário').nullish(),
  synchronizedAt: dateOrIsoString().describe('Data de sincronização do questionário').nullish(),
  questions: z.array(questionObject),
})

export const answerObject = z.object({
  id: z.string().describe('Identificador da resposta').nullish(),
  answerId: z.number().describe('Id da resposta').nullish(),
  questionId: z.number().describe('Id da questão respondida'),
  string: z.string().describe('Valor da resposta em formato de texto').nullable().default(null),
  boolean: z
    .boolean()
    .describe('Valor da resposta em formato de verdadeiro ou falso')
    .nullable()
    .default(null),
  numeric: z.number().describe('Valor da resposta em formato numérico').nullable().default(null),
  array: z
    .array(z.string())
    .describe('Valor da resposta em formato de conjunto de respostas')
    .nullable()
    .default(null),
  date: dateOrIsoString().describe('Valor da resposta em formato de data').nullable().default(null),
})
